import { REQ } from "constants/api/requests"
import { IResponse } from "constants/interfaces"

import useApi from "util/api"

import {
  BulkGrantFilters,
  BulkGrantJob,
  BulkGrantPreview,
  RecipientPolicy,
  TokenAnalytics,
  TokenConfig,
  TokenConfigChange,
  TokenConfigPatch,
  TokenDistribution,
  TokenMutationResult,
  TokenPagination,
  TokenPool,
  TokenPoolPatch,
  TokenUserHolding
} from "./type"

/**
 * Admin $TOKEN API. Thin wrapper over `/admin/token/*`, which the monolith
 * proxies to token-service over gRPC.
 *
 * `tenant` is a query param on EVERY call — the monolith resolves the gate
 * (`tokenEnabled && isMicroserviceOwner`) from it, so omitting it does not fall
 * back to a default tenant, it fails.
 *
 * ⚠️ EVERY METHOD RETURNS `TokenMutationResult`, READS INCLUDED. That deviates
 * from the house idiom (return `undefined`, fire a toast) on purpose.
 *
 * This module has THREE failure modes that must be told apart on screen, and a
 * toast can express none of them:
 *
 *   403 TOKEN_DISABLED — the gate is closed for this tenant. A configuration
 *                        state; it will not resolve by waiting.
 *   503                — token-service is unreachable. A real outage; retry.
 *   business code      — PURCHASE_FINAL, INSUFFICIENT_TOKEN, REASON_REQUIRED.
 *                        The code IS the explanation the operator needs.
 *
 * Collapsing those into `undefined` would render "no data" for a closed gate,
 * which reads as "this tenant has no presale" — the single most misleading
 * answer available. So the code comes back and the caller decides.
 */

/**
 * Normalise a rejected request into a result the caller can render.
 *
 * The axios interceptor rejects with `error.response`, so the payload may sit at
 * `error.data` (already unwrapped) or `error.response.data` (raw). `status` is
 * carried because the 503 case has no stable code string — the monolith answers
 * AUTHORITY_UNAVAILABLE or AUTHORITATIVE_SOURCE_UNAVAILABLE depending on the
 * endpoint, so status is the reliable signal there.
 */
const asFailure = <T,>(error: any): TokenMutationResult<T> => ({
  ok: false,
  status: error?.status ?? error?.response?.status,
  code: error?.data?.code ?? error?.response?.data?.code,
  message: error?.data?.message ?? error?.response?.data?.message
})

export const AdminTokenService = {
  // ── Config ────────────────────────────────────────────────────────────────

  async getConfig(tenant: string): Promise<TokenMutationResult<TokenConfig>> {
    try {
      const { data } = await useApi.get<IResponse<TokenConfig>>(REQ.ADMIN_DASHBOARD.TOKEN.CONFIG, { params: { tenant } })
      if (data?.success) return { ok: true, data: data.response }
      return { ok: false }
    } catch (error) {
      return asFailure(error)
    }
  },

  /**
   * `reason` is mandatory and the server rejects a blank one with REASON_REQUIRED.
   *
   * Business B3 declined admin 2FA on the strength of "role + reason + audit log",
   * which makes this string a control rather than a note. The UI blocks submit on
   * an empty one only to avoid a guaranteed-failing round trip — the enforcement
   * is server-side and stays there.
   */
  async updateConfig(tenant: string, reason: string, patch: TokenConfigPatch): Promise<TokenMutationResult<TokenConfigChange[]>> {
    try {
      const { data } = await useApi.put<IResponse<{ changes: TokenConfigChange[] }>>(REQ.ADMIN_DASHBOARD.TOKEN.CONFIG, { reason, ...patch }, { params: { tenant } })
      if (data?.success) return { ok: true, data: data.response.changes }
      return { ok: false }
    } catch (error) {
      return asFailure(error)
    }
  },

  async listConfigChanges(tenant: string, page = 1, limit = 20): Promise<TokenMutationResult<{ changes: TokenConfigChange[]; pagination: TokenPagination }>> {
    try {
      const { data } = await useApi.get<IResponse<{ changes: TokenConfigChange[]; pagination: TokenPagination }>>(REQ.ADMIN_DASHBOARD.TOKEN.CONFIG_CHANGES, {
        params: { tenant, page, limit }
      })
      if (data?.success) return { ok: true, data: data.response }
      return { ok: false }
    } catch (error) {
      return asFailure(error)
    }
  },

  // ── Pools ─────────────────────────────────────────────────────────────────

  async updatePool(tenant: string, reason: string, poolId: string, patch: TokenPoolPatch): Promise<TokenMutationResult<TokenPool>> {
    try {
      const { data } = await useApi.put<IResponse<{ pool: TokenPool }>>(REQ.ADMIN_DASHBOARD.TOKEN.POOL, { reason, pool_id: poolId, ...patch }, { params: { tenant } })
      if (data?.success) return { ok: true, data: data.response.pool }
      return { ok: false }
    } catch (error) {
      return asFailure(error)
    }
  },

  /**
   * `idempotencyKey` MUST be generated once per modal open and reused across
   * retries of the same intent — that is the entire mechanism preventing a
   * double-click from funding a pool twice. Generating it per submit would make
   * the key decorative. See `useIdempotencyKey`.
   */
  async topUpPool(tenant: string, reason: string, poolId: string, amount: string, idempotencyKey: string): Promise<TokenMutationResult<{ new_pool_balance: string }>> {
    try {
      const { data } = await useApi.post<IResponse<{ new_pool_balance: string }>>(
        REQ.ADMIN_DASHBOARD.TOKEN.POOL_TOP_UP,
        { reason, pool_id: poolId, amount, idempotency_key: idempotencyKey },
        { params: { tenant } }
      )
      if (data?.success) return { ok: true, data: data.response }
      return { ok: false }
    } catch (error) {
      return asFailure(error)
    }
  },

  // ── Distributions ─────────────────────────────────────────────────────────

  async listDistributions(tenant: string, poolId?: string, page = 1, limit = 20): Promise<TokenMutationResult<{ distributions: TokenDistribution[]; pagination: TokenPagination }>> {
    try {
      const { data } = await useApi.get<IResponse<{ distributions: TokenDistribution[]; pagination: TokenPagination }>>(REQ.ADMIN_DASHBOARD.TOKEN.DISTRIBUTIONS, {
        params: { tenant, page, limit, ...(poolId && { pool_id: poolId }) }
      })
      if (data?.success) return { ok: true, data: data.response }
      return { ok: false }
    } catch (error) {
      return asFailure(error)
    }
  },

  /**
   * Run a distribution off-cycle.
   *
   * `already_distributed: true` is a SUCCESS, not a failure — it means the
   * period's guard worked and nobody was paid twice. The UI must say so rather
   * than showing an error.
   */
  async triggerDistribution(tenant: string, reason: string, poolId?: string): Promise<TokenMutationResult<{ distributions: TokenDistribution[]; already_distributed: boolean }>> {
    try {
      const { data } = await useApi.post<IResponse<{ distributions: TokenDistribution[]; already_distributed: boolean }>>(
        REQ.ADMIN_DASHBOARD.TOKEN.DISTRIBUTIONS_TRIGGER,
        { reason, ...(poolId && { pool_id: poolId }) },
        { params: { tenant } }
      )
      if (data?.success) return { ok: true, data: data.response }
      return { ok: false }
    } catch (error) {
      return asFailure(error)
    }
  },

  // ── Analytics ─────────────────────────────────────────────────────────────

  /**
   * `from`/`to` filter SALES and DISTRIBUTIONS only. `total_staked` and
   * `active_positions` are point-in-time state, not flows — there is no
   * meaningful "total staked between March and April". The UI labels them so.
   */
  async getAnalytics(tenant: string, from?: string, to?: string): Promise<TokenMutationResult<TokenAnalytics>> {
    try {
      const { data } = await useApi.get<IResponse<TokenAnalytics>>(REQ.ADMIN_DASHBOARD.TOKEN.ANALYTICS, {
        params: { tenant, ...(from && { from }), ...(to && { to }) }
      })
      if (data?.success) return { ok: true, data: data.response }
      return { ok: false }
    } catch (error) {
      return asFailure(error)
    }
  },

  // ── Player lookup ─────────────────────────────────────────────────────────

  /**
   * One player's holding. Read-only — no reason, no idempotency key.
   *
   * Returns a `TokenMutationResult` like everything else here, because the
   * caller must tell a closed gate (this tenant has no $TOKEN) apart from an
   * outage (token-service down) apart from a player who simply holds none. All
   * three would otherwise render as an empty balance, and only one of them is
   * true.
   */
  async getUserHolding(tenant: string, userId: string): Promise<TokenMutationResult<TokenUserHolding>> {
    try {
      const { data } = await useApi.get<IResponse<TokenUserHolding>>(
        REQ.ADMIN_DASHBOARD.TOKEN.USER.replace(":userId", encodeURIComponent(userId)),
        { params: { tenant } }
      )
      if (data?.success) return { ok: true, data: data.response }
      return { ok: false }
    } catch (error) {
      return asFailure(error)
    }
  },

  // ── Manual operations ─────────────────────────────────────────────────────
  //
  // 🔴 Spec §8.4: these three are how a casino gets hit with insider abuse if
  // access is too loose. Every one is idempotent on a key the CALLER owns.

  async adjustBalance(
    tenant: string,
    reason: string,
    userId: string,
    amount: string,
    idempotencyKey: string
  ): Promise<TokenMutationResult<{ new_token_balance: string; ledger_id: string }>> {
    try {
      const { data } = await useApi.post<IResponse<{ new_token_balance: string; ledger_id: string }>>(
        REQ.ADMIN_DASHBOARD.TOKEN.ADJUST_BALANCE,
        { reason, user_id: userId, amount, idempotency_key: idempotencyKey },
        { params: { tenant } }
      )
      if (data?.success) return { ok: true, data: data.response }
      return { ok: false }
    } catch (error) {
      return asFailure(error)
    }
  },

  /**
   * ⚠️ NOT A REFUND. Business A2: token purchases are final, no discretionary
   * refunds ever. This recovers a SYSTEM ERROR — money taken, tokens not
   * credited. The server rejects a COMPLETED purchase with `PURCHASE_FINAL`, and
   * that rejection is the enforcement; the UI must surface the code rather than
   * flattening it into "something went wrong".
   */
  async reconcilePurchase(
    tenant: string,
    reason: string,
    purchaseId: string,
    idempotencyKey: string
  ): Promise<TokenMutationResult<{ usd_returned_cents: string; token_removed: string; fs_transaction_id: string; purchase_status: string }>> {
    try {
      const { data } = await useApi.post<IResponse<{ usd_returned_cents: string; token_removed: string; fs_transaction_id: string; purchase_status: string }>>(
        REQ.ADMIN_DASHBOARD.TOKEN.RECONCILE_PURCHASE,
        { reason, purchase_id: purchaseId, idempotency_key: idempotencyKey },
        { params: { tenant } }
      )
      if (data?.success) return { ok: true, data: data.response }
      return { ok: false }
    } catch (error) {
      return asFailure(error)
    }
  },

  /**
   * ❌ NOT an early-unstake feature. There is no early unstake. This is a manual
   * ledger operation with an audit trail, for positions that should not exist.
   */
  async terminatePosition(
    tenant: string,
    reason: string,
    positionId: string,
    includeAccruedReward: boolean,
    idempotencyKey: string
  ): Promise<TokenMutationResult<{ principal_returned: string; rewards_paid: string; new_token_balance: string }>> {
    try {
      const { data } = await useApi.post<IResponse<{ principal_returned: string; rewards_paid: string; new_token_balance: string }>>(
        REQ.ADMIN_DASHBOARD.TOKEN.TERMINATE_POSITION,
        { reason, position_id: positionId, include_accrued_reward: includeAccruedReward, idempotency_key: idempotencyKey },
        { params: { tenant } }
      )
      if (data?.success) return { ok: true, data: data.response }
      return { ok: false }
    } catch (error) {
      return asFailure(error)
    }
  },

  // ── Bulk grant campaigns ────────────────────────────────────────────────────
  // Behind their own permission (`page.token.bulk_grant`), which PAGE_TOKEN does
  // not imply. Starting or cancelling additionally needs
  // `token.bulk_grant.execute`, checked server-side.

  /** Audience size for the given filters. Drives the preview and the confirm phrase. */
  async previewBulkGrant(tenant: string, filters: BulkGrantFilters): Promise<TokenMutationResult<BulkGrantPreview>> {
    try {
      const { data } = await useApi.post<IResponse<BulkGrantPreview>>(
        REQ.ADMIN_DASHBOARD.TOKEN.BULK_GRANT_PREVIEW,
        { filters },
        { params: { tenant } }
      )
      if (data?.success) return { ok: true, data: data.response }
      return { ok: false }
    } catch (error) {
      return asFailure(error)
    }
  },

  /**
   * Open a campaign.
   *
   * `confirmedTotalUsers` is the audience size the operator actually saw. The
   * server recounts and refuses when the two disagree, so a stale preview cannot
   * authorise a campaign against a different-sized audience.
   */
  async createBulkGrant(
    tenant: string,
    reason: string,
    amount: string,
    filters: BulkGrantFilters,
    recipientPolicy: RecipientPolicy,
    confirmedTotalUsers: number,
    idempotencyKey: string
  ): Promise<TokenMutationResult<{ job: BulkGrantJob }>> {
    try {
      const { data } = await useApi.post<IResponse<{ job: BulkGrantJob }>>(
        REQ.ADMIN_DASHBOARD.TOKEN.BULK_GRANT,
        {
          reason,
          amount,
          filters,
          recipient_policy: recipientPolicy,
          confirmed_total_users: confirmedTotalUsers,
          idempotency_key: idempotencyKey
        },
        { params: { tenant } }
      )
      if (data?.success) return { ok: true, data: data.response }
      return { ok: false }
    } catch (error) {
      return asFailure(error)
    }
  },

  /** The active campaign, if any, plus the cooldown countdown. */
  async getActiveBulkGrant(
    tenant: string
  ): Promise<TokenMutationResult<{ job?: BulkGrantJob; cooldown_seconds_remaining?: number }>> {
    try {
      const { data } = await useApi.get<IResponse<{ job?: BulkGrantJob; cooldown_seconds_remaining?: number }>>(
        REQ.ADMIN_DASHBOARD.TOKEN.BULK_GRANT_ACTIVE,
        { params: { tenant } }
      )
      if (data?.success) return { ok: true, data: data.response }
      return { ok: false }
    } catch (error) {
      return asFailure(error)
    }
  },

  async listBulkGrants(tenant: string): Promise<TokenMutationResult<{ jobs: BulkGrantJob[]; total: number }>> {
    try {
      const { data } = await useApi.get<IResponse<{ jobs: BulkGrantJob[]; total: number }>>(
        REQ.ADMIN_DASHBOARD.TOKEN.BULK_GRANT,
        { params: { tenant } }
      )
      if (data?.success) return { ok: true, data: data.response }
      return { ok: false }
    } catch (error) {
      return asFailure(error)
    }
  },

  /**
   * Stop a campaign at the next chunk boundary.
   *
   * Players already credited keep their tokens — there is no compensating debit,
   * and the UI says so before this is called.
   */
  async cancelBulkGrant(tenant: string, jobId: string, reason: string): Promise<TokenMutationResult<{ job: BulkGrantJob }>> {
    try {
      const { data } = await useApi.post<IResponse<{ job: BulkGrantJob }>>(
        REQ.ADMIN_DASHBOARD.TOKEN.BULK_GRANT_CANCEL.replace(":jobId", jobId),
        { reason },
        { params: { tenant } }
      )
      if (data?.success) return { ok: true, data: data.response }
      return { ok: false }
    } catch (error) {
      return asFailure(error)
    }
  },

}
